import { useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet } from "lucide-react";
import { useApi, useFetch, lastMonth } from "../lib/api";
import { Pill, Empty } from "./shared";
import { Button } from "./ui/button";
import { MonthPicker } from "./DateField";

export const HoldReport = ({ eid }) => {
  const api = useApi();
  const [month, setMonth] = useState(lastMonth());
  const params = `month=${month}${eid ? `&eid=${eid}` : ""}`;
  const { data } = useFetch(`/pe/hold-report?${params}`);
  const xlsx = async () => {
    try {
      const r = await api.get(`/pe/hold-report.xlsx?${params}`, { responseType: "blob" });
      const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = `Hold_Report_${month}.xlsx`; a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 1000);
    } catch { toast.error("Download failed"); }
  };
  const t = data?.totals;
  return (
    <div className="grid gap-4" data-testid="hold-report">
      <div className="flex flex-wrap items-end gap-3">
        <div className="mr-auto"><div className="c-eyebrow">Hold report</div><div className="text-xs text-slate-500 mt-1">Payment holds placed, released and days on hold per contractor for the wage month.</div></div>
        <label className="grid gap-1"><span className="text-xs text-slate-400">Wage month</span>
          <MonthPicker value={month} onChange={setMonth} testid="hold-report-month" className="w-44" /></label>
        <Button variant="outline" className="border-[#1F2A3E] hover:bg-[#1A2234] hover:text-white" onClick={xlsx} data-testid="hold-report-xlsx"><FileSpreadsheet size={14} className="mr-1.5" />Excel</Button>
      </div>
      {!data ? <p className="text-xs text-slate-500 font-mono">Loading…</p> : !data.rows.length ? <Empty text={`No payment holds for ${month}.`} testid="hold-report-empty" /> : <>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[["Holds placed", t.placed], ["Released", t.released], ["Still on hold", t.open], ["Total days on hold", t.days_on_hold]].map(([l, v]) => (
            <div key={l} className="c-surface p-4" data-testid={`hold-report-total-${l.toLowerCase().replace(/\s/g, "-")}`}><div className="text-xs text-slate-400">{l}</div><div className="font-display text-2xl font-semibold mt-1">{v}</div></div>))}
        </div>
        <div className="c-surface overflow-x-auto">
          <table className="c-table w-full">
            <thead><tr><th>Contractor</th><th>Principal employer</th><th>Placed</th><th>Released</th><th>Still on hold</th><th>Days on hold</th></tr></thead>
            <tbody>{data.contractors.map((c) => (
              <tr key={c.vendor_id} data-testid="hold-report-contractor-row"><td className="font-medium">{c.vendor_name}</td><td className="text-xs">{c.employer_name || "—"}</td>
                <td>{c.placed}</td><td>{c.released}</td><td>{c.open ? <span className="text-rose-300">{c.open}</span> : 0}</td><td className="font-mono">{c.days_on_hold}</td></tr>))}</tbody>
          </table>
        </div>
        <div className="c-surface overflow-x-auto">
          <table className="c-table w-full">
            <thead><tr><th>Contractor</th><th>Reason</th><th>Placed on</th><th>Status</th><th>Released on</th><th>Days</th><th>Release note</th></tr></thead>
            <tbody>{data.rows.map((r) => (
              <tr key={r.id} data-testid="hold-report-row"><td className="text-sm">{r.vendor_name}</td>
                <td className="text-xs max-w-xs"><div className="font-mono text-[11px] text-slate-400">{r.reason_code}{r.auto ? " · auto" : ""}</div>{r.reason}</td>
                <td className="font-mono text-xs">{r.placed_on || "—"}</td><td><Pill tone={r.status === "Released" ? "green" : "red"}>{r.status}</Pill></td>
                <td className="font-mono text-xs">{r.released_on || "—"}</td><td className="font-mono">{r.days_on_hold ?? "—"}</td><td className="text-xs text-slate-400">{r.release_note || "—"}</td></tr>))}</tbody>
          </table>
        </div>
      </>}
    </div>);
};
